// ZUSTAND→ATOM off-ramp (docs/projects/active/effect-native-audio-chunk-player-orchestration.md)
// Status: converted
// useAudioChunkSelector keeps its signature over useAtomValue(store.atom,
// selector); keep selectors returning primitives/stable refs.
import { RegistryContext, useAtomValue } from '@effect/atom-react';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { AtomRegistry } from 'effect/unstable/reactivity';
import { Effect, type Fiber } from 'effect';
import { assertDefined, formatTimeSeconds } from '@listening/utils/ts-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { use, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  estimateDocPackReadingDurationMs,
  docPackAnchorForPageReadableCharOffset,
  docPackDocumentPositionMsForLoadedPageAnchor,
  docPackDocumentPositionMsForAnchor,
  docPackReadablePageOffsetForDocumentPositionMs,
  type DocPackReadingAnchor,
} from '../doc-pack/reading-plan';
import {
  docPackCatalogArgs,
  docPackPageArgs,
  isDocPackVersionUsable,
} from '../doc-pack/source';
import { useAppRuntime, useEq } from '../query/runtime-context';
import { useDocPackReadingClassOverrides } from '../stores/doc-pack-reading-preferences-store';
import { effectiveChunkDurationMs } from './chunk-duration';
import {
  AudioChunkPlayerContext,
  type AudioChunkPlayerActions,
  type AudioChunkPlayerState,
  useCurrentAudioChunkQuery,
  useDocumentSummary,
  usePreferedVoice,
  useTtsChunkAudioDurationMsCacheStore,
} from './core';
import { useAudioPoolPlayerContext } from './pool-player-context';

export const useAudioChunkPlayer = () => {
  const { store, storeActions } = useContextAndErrorIfNull(
    AudioChunkPlayerContext,
  );
  const player = useAudioPoolPlayerContext();

  const currentAudioChunkQuery = useCurrentAudioChunkQuery();

  const isErrored = currentAudioChunkQuery.isError;
  const ttsId = useAtomValue(store.atom, (state) => state.ttsId);
  const docPackChunk = useAtomValue(store.atom, (state) => state.docPackChunk);
  const _isLoadingAudio = useAtomValue(
    store.atom,
    (state) => state.isLoadingAudio,
  );

  const isLoadingAudio = Boolean(
    (ttsId ?? docPackChunk) &&
    !player.isPlaying &&
    (_isLoadingAudio || currentAudioChunkQuery.isPending || player.isLoading),
  );

  return {
    store,
    isErrored,
    isLoadingAudio,
    player,
    pause: storeActions.pause,
    play: storeActions.play,
    togglePlayPause: storeActions.togglePlayPause,
    loadAudio: storeActions.loadAudio,
    loadAudioFromProgress: storeActions.loadAudioFromProgress,
    loadAudioAtNote: storeActions.loadAudioAtNote,
    unloadAudio: storeActions.unloadAudio,
    skipToChapterStart: storeActions.skipToChapterStart,
    skipToChunk: storeActions.skipToChunk,
    skipToDocPackRange: storeActions.skipToDocPackRange,
    reloadChunk: storeActions.reloadChunk,
  };
};

export function useAudioChunkSelector<T>(
  selector: (state: AudioChunkPlayerState) => T,
): T {
  const { store } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  return useAtomValue(store.atom, selector);
}

/** The store's imperative actions with zero store subscriptions — for
 * render-time action access in leaf components (e.g. click handlers) that must
 * not re-render on playback state changes. */
/** Dispatch surface. Every method records intent and returns immediately;
 * none is awaitable. Observe outcomes through player state, or through
 * `useAfterPlaybackCommand` when settlement is genuinely needed. */
export function useAudioChunkActions(): AudioChunkPlayerActions {
  const { storeActions } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  return storeActions;
}

/**
 * Runs `onSettled` once the playback command in flight succeeds.
 *
 * For the read-along seek flows, whose follow controller must re-engage only
 * after the seek has actually landed — engaging earlier snaps the view to the
 * chunk the user just left.
 *
 * Supersession is not silent: a superseded fiber publishes no result, so the
 * observer survives and `onSettled` runs when the command that REPLACED this
 * one succeeds. That is what the seek flows want — the newer seek is the one
 * worth following — but a caller that needs per-command settlement would have
 * to carry its own identity. A failed command runs nothing.
 */
export function useAfterPlaybackCommand() {
  const { commandResult } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  const registry = use(RegistryContext);
  const runtime = useAppRuntime();
  const pendingRef = useRef<Fiber.Fiber<void, unknown> | null>(null);
  // The only unavoidable synchronisation here: a fiber that outlives the
  // component would call back into an unmounted follow controller.
  useEffect(
    () => () => {
      pendingRef.current?.interruptUnsafe();
      pendingRef.current = null;
    },
    [],
  );
  return useCallback(
    (onSettled: () => void) => {
      // One observer at a time: a second seek replaces the first rather than
      // leaving it to fire on an unrelated later command.
      pendingRef.current?.interruptUnsafe();
      pendingRef.current = runtime.runFork(
        AtomRegistry.getResult(registry, commandResult, {
          suspendOnWaiting: true,
        }).pipe(
          Effect.andThen(Effect.sync(onSettled)),
          // A failed or superseded command runs nothing.
          Effect.ignore,
        ),
      );
    },
    [commandResult, registry, runtime],
  );
}

export const useAudioChunkPlayerSeekMethods = () => {
  const player = useAudioPoolPlayerContext();
  const { store, storeActions, docPackEnabled, docPackPageFetchMode } =
    useContextAndErrorIfNull(AudioChunkPlayerContext);
  const queryClient = useQueryClient();
  const eq = useEq();
  const runtime = useAppRuntime();

  const { documentSummary } = useDocumentSummary();

  const ttsId = useAtomValue(store.atom, (state) => state.ttsId);
  const chapterId = useAtomValue(store.atom, (state) => state.chapterId);
  const playbackMode = useAtomValue(store.atom, (state) => state.playbackMode);
  const docPackChunk = useAtomValue(store.atom, (state) => state.docPackChunk);
  const realTtsAudioDurationMsMapGetMs = useTtsChunkAudioDurationMsCacheStore(
    (s) => s.getMs,
  );
  const docPackClassOverrides = useDocPackReadingClassOverrides(
    documentSummary?.id,
  );
  const docPack = documentSummary?.doc_pack;
  const docPackCatalogQuery = useQuery({
    ...eq.queryOptions(
      docPackCatalogArgs(documentSummary?.id ?? '', docPack?.url ?? ''),
    ),
    enabled:
      playbackMode === 'doc-pack' &&
      documentSummary !== undefined &&
      docPackEnabled &&
      isDocPackVersionUsable(docPack),
  });
  const docPackCatalog = docPackCatalogQuery.data?.catalog;
  const currentDocPackChunkFirstRange = docPackChunk?.ranges[0];
  const currentDocPackChunkPageQuery = useQuery({
    queryKey: [
      'doc-pack-current-page',
      documentSummary?.id ?? '',
      docPackCatalogQuery.data?.url ?? '',
      currentDocPackChunkFirstRange?.[0] ?? 0,
    ] as const,
    queryFn: () => {
      if (!docPackCatalogQuery.data || !currentDocPackChunkFirstRange) {
        throw new Error(
          'doc-pack current page query disabled without pack/page',
        );
      }
      return queryClient.ensureQueryData(
        eq.queryOptions(
          docPackPageArgs(
            documentSummary?.id ?? '',
            docPackCatalogQuery.data,
            currentDocPackChunkFirstRange[0],
            { fetchMode: docPackPageFetchMode },
          ),
        ),
      );
    },
    enabled:
      playbackMode === 'doc-pack' &&
      docPackEnabled &&
      docPackCatalogQuery.data !== undefined &&
      currentDocPackChunkFirstRange !== undefined &&
      documentSummary !== undefined,
  });

  // The SAME resolution the engine uses to fetch chunks and record measured
  // durations, so the duration-cache reads below can never key on a different
  // voice than the one playing.
  const voice = usePreferedVoice().committedVoice.name;

  const durationsPrefixSums = useMemo(
    () =>
      documentSummary
        ? documentSummary.chapters.reduce<Array<Array<number>>>(
            (acc, chapter, chapterIdx) => {
              // Prefix sums are cumulative across chapters, so the running
              // total is the last value emitted so far. `acc.flat().at(-1)`
              // carries that across an empty chapter (no tts_ids → []),
              // which would otherwise make `.at(-1)` undefined and crash.
              const prevChapterDuration =
                chapterIdx === 0 ? 0 : (acc.flat().at(-1) ?? 0);

              const currentChapterChunkSums = chapter.tts_ids.reduce<
                Array<number>
              >((acc, ttsId, idx) => {
                const last =
                  idx === 0 ? prevChapterDuration : (acc.at(-1) ?? 0);
                const chunkMs = effectiveChunkDurationMs(
                  realTtsAudioDurationMsMapGetMs(ttsId, voice),
                  chapter.avg_estimated_tts_chunk_duration_ms,
                );
                return [...acc, last + chunkMs];
              }, []);
              return [...acc, currentChapterChunkSums];
            },
            [],
          )
        : [],
    [documentSummary, realTtsAudioDurationMsMapGetMs, voice],
  );

  const seekToMsInDoc = useCallback(
    (value: number) => {
      if (
        playbackMode === 'doc-pack' &&
        docPackCatalog &&
        docPackCatalogQuery.data
      ) {
        const pageOffset = docPackReadablePageOffsetForDocumentPositionMs(
          docPackCatalog,
          value,
          docPackClassOverrides,
        );
        if (!pageOffset) return;
        void (async () => {
          // pageOffset.readableCharOffset is READABLE space (skipped segments
          // excluded); anchor.charStart is an ABSOLUTE offset into the full
          // page text. Converting requires the page's segment layout, so the
          // anchor is only ever built from a loaded page.
          const anchorFromLoadedPage = async () => {
            const page = await queryClient.ensureQueryData(
              eq.queryOptions(
                docPackPageArgs(
                  documentSummary?.id ?? '',
                  docPackCatalogQuery.data,
                  pageOffset.page,
                  { fetchMode: docPackPageFetchMode },
                ),
              ),
            );
            return docPackAnchorForPageReadableCharOffset(
              page,
              pageOffset.readableCharOffset,
              docPackClassOverrides,
            );
          };
          let anchor: DocPackReadingAnchor | undefined;
          try {
            anchor = await anchorFromLoadedPage().catch((error: unknown) => {
              runtime.runFork(
                Effect.logWarning(
                  'doc-pack seek page load failed; retrying once',
                ).pipe(Effect.annotateLogs({ error, page: pageOffset.page })),
              );
              return anchorFromLoadedPage();
            });
          } catch (error: unknown) {
            runtime.runFork(
              Effect.logWarning(
                'doc-pack seek page load failed twice; falling back to page start',
              ).pipe(Effect.annotateLogs({ error, page: pageOffset.page })),
            );
            // Without the page the readable offset cannot be converted, so
            // seek to the page start: charStart 0 is valid absolute space and
            // the chunk lookup clamps it to the page's first readable chunk.
            anchor = { page: pageOffset.page, charStart: 0 };
          }
          if (!anchor) return;
          runtime.runFork(
            Effect.logDebug('Seeking to doc-pack document position').pipe(
              Effect.annotateLogs({ anchor, value }),
            ),
          );
          storeActions.skipToDocPackRange(anchor);
        })();
        return;
      }

      const chapterIdx = durationsPrefixSums.findIndex((chapterPrefixSums) => {
        const last = chapterPrefixSums.at(-1) ?? 0;
        return last >= value;
      });
      if (chapterIdx === -1) return;
      const currentPrefixSums = durationsPrefixSums[chapterIdx] ?? [0];
      const ttsIdx = currentPrefixSums.findIndex((ttsEnd) => ttsEnd >= value);
      if (ttsIdx === -1) return;
      const seekedTtsId = assertDefined(
        documentSummary?.chapters[chapterIdx]?.tts_ids[ttsIdx],
      );
      const prevTtsIdx = ttsIdx - 1;
      const prevPrefixSums = durationsPrefixSums[chapterIdx - 1] ?? [0];
      const lastInPrevSums = prevPrefixSums.at(-1) ?? 0;
      const offsetIntoTtsChunk =
        value -
        (prevTtsIdx === -1
          ? lastInPrevSums
          : (currentPrefixSums[prevTtsIdx] ?? 0));

      if (ttsId !== seekedTtsId) {
        runtime.runFork(
          Effect.logDebug('Seeking to different chunk').pipe(
            Effect.annotateLogs({ chapterIdx, ttsIdx, offsetIntoTtsChunk }),
          ),
        );
        storeActions.skipToChunk(seekedTtsId, {
          loadPos: { posMs: offsetIntoTtsChunk },
        });
      } else {
        runtime.runFork(
          Effect.logDebug(
            `Seeking within same chunk ${formatTimeSeconds(offsetIntoTtsChunk / 1000)}`,
          ),
        );
        player.seek(offsetIntoTtsChunk / 1000);
      }
    },
    [
      documentSummary?.chapters,
      documentSummary?.id,
      durationsPrefixSums,
      docPackCatalog,
      docPackCatalogQuery.data,
      docPackClassOverrides,
      docPackPageFetchMode,
      eq,
      player,
      playbackMode,
      queryClient,
      runtime,
      storeActions,
      ttsId,
    ],
  );

  const lastChapterChunkSums = durationsPrefixSums.at(-1) ?? [0];
  // Fall back to 0 for an empty final chapter, matching the `?? 0` guards used
  // throughout this block (an empty chapter would make `.at(-1)` undefined).
  const legacyEstimatedTotalAudioDuration = lastChapterChunkSums.at(-1) ?? 0;
  const docPackEstimatedTotalAudioDuration = docPackCatalog
    ? estimateDocPackReadingDurationMs(docPackCatalog, docPackClassOverrides)
    : 0;
  const estimatedTotalAudioDuration =
    playbackMode === 'doc-pack' && docPackCatalog
      ? docPackEstimatedTotalAudioDuration
      : legacyEstimatedTotalAudioDuration;

  const currentChapterIdx = (() => {
    if (!documentSummary || !chapterId) return undefined;
    const idx = documentSummary.chapters.findIndex((c) => c.id === chapterId);
    return idx === -1 ? undefined : idx;
  })();
  const currentTtsIdx = (() => {
    if (!documentSummary || !ttsId || currentChapterIdx === undefined)
      return undefined;
    const idx = assertDefined(
      documentSummary.chapters[currentChapterIdx],
    ).tts_ids.indexOf(ttsId);
    // `loadAudio` writes the caller-supplied {chapterId, ttsId} to the store
    // before validating/normalizing them (only after its first await). A render
    // that lands in that window can see a ttsId that isn't in the chapter's
    // tts_ids yet (indexOf === -1). Treat that transient mismatch as "no
    // current chunk" rather than throwing off the render path (which white-
    // screens the tree); the store settles and re-renders correctly.
    return idx === -1 ? undefined : idx;
  })();

  const currentChapterChunkSums =
    currentChapterIdx !== undefined
      ? assertDefined(durationsPrefixSums[currentChapterIdx])
      : [];
  const prevChapterChunkSums =
    currentChapterIdx !== undefined
      ? (durationsPrefixSums[currentChapterIdx - 1] ?? [])
      : [];
  const legacyCurrentChunkStartMs =
    currentTtsIdx !== undefined
      ? currentTtsIdx === 0
        ? (prevChapterChunkSums.at(-1) ?? 0)
        : (currentChapterChunkSums[currentTtsIdx - 1] ?? 0)
      : 0;
  const currentDocPackChunkMsStart =
    docPackCatalog && currentDocPackChunkFirstRange
      ? currentDocPackChunkPageQuery.data
        ? docPackDocumentPositionMsForLoadedPageAnchor(
            docPackCatalog,
            currentDocPackChunkPageQuery.data,
            {
              page: currentDocPackChunkFirstRange[0],
              charStart: currentDocPackChunkFirstRange[1],
            },
            docPackClassOverrides,
          )
        : docPackDocumentPositionMsForAnchor(
            docPackCatalog,
            {
              page: currentDocPackChunkFirstRange[0],
              charStart: currentDocPackChunkFirstRange[1],
            },
            docPackClassOverrides,
          )
      : 0;
  const currentChunkMsStart =
    playbackMode === 'doc-pack' && docPackCatalog
      ? currentDocPackChunkMsStart
      : legacyCurrentChunkStartMs;

  const seekRelative = useCallback(
    (relativeMs: number) => {
      const currentAudioPosInDocument =
        currentChunkMsStart + player.getPositionMs();
      seekToMsInDoc(currentAudioPosInDocument + relativeMs);
    },
    [currentChunkMsStart, player, seekToMsInDoc],
  );

  return {
    seekRelative,
    seekToMsInDoc,
    currentChunkStartMsInDoc: currentChunkMsStart,
    estimatedTotalAudioDuration,
    durationsPrefixSums,
  };
};
